import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../api';

const EMPTY_FORM = {
  keyword: '',
  flow_id: '',
  flow_name: '',
  flow_cta: 'Open Form',
  flow_header: '',
  flow_body: 'Please fill out the form below.',
  active: true,
};

const STATUS_COLORS = {
  PUBLISHED: { bg: 'rgba(34,197,94,0.12)', color: 'var(--success)', label: 'Published' },
  DRAFT:     { bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)', label: 'Draft' },
  DEPRECATED:{ bg: 'rgba(100,116,139,0.12)', color: 'var(--text-muted)', label: 'Deprecated' },
};

export default function FlowTriggers() {
  const [triggers, setTriggers]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  // Flow picker state
  const [metaFlows, setMetaFlows]       = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [flowsError, setFlowsError]     = useState('');
  const [manualId, setManualId]         = useState(false);
  const [flowSearch, setFlowSearch]     = useState('');

  const fetchTriggers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/flow-triggers');
      setTriggers(r.data);
    } catch { toast.error('Failed to load triggers'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTriggers(); }, [fetchTriggers]);

  async function loadMetaFlows() {
    setFlowsLoading(true);
    setFlowsError('');
    try {
      const r = await api.get('/facebook/flows');
      setMetaFlows(r.data.flows || []);
      if ((r.data.flows || []).length === 0) {
        setFlowsError('No flows found on your Meta account. Create one in WhatsApp Manager first, or enter the ID manually.');
      }
    } catch (err) {
      setFlowsError(err.response?.data?.error || 'Could not load flows from Meta.');
    }
    setFlowsLoading(false);
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setManualId(false);
    setFlowSearch('');
    setMetaFlows([]);
    setFlowsError('');
    setShowModal(true);
    // Auto-load flows when modal opens
    loadMetaFlows();
  }

  function openEdit(t) {
    setEditing(t);
    setForm({
      keyword:     t.keyword,
      flow_id:     t.flow_id,
      flow_name:   t.flow_name,
      flow_cta:    t.flow_cta || 'Open Form',
      flow_header: t.flow_header || '',
      flow_body:   t.flow_body || '',
      active:      t.active !== false,
    });
    setManualId(false);
    setFlowSearch('');
    setMetaFlows([]);
    setFlowsError('');
    setShowModal(true);
    loadMetaFlows();
  }

  function selectFlow(flow) {
    setForm(f => ({ ...f, flow_id: flow.id, flow_name: flow.name }));
    setFlowSearch('');
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const r = await api.put(`/flow-triggers/${editing._id}`, form);
        setTriggers(ts => ts.map(t => t._id === editing._id ? r.data : t));
        toast.success('Trigger updated');
      } else {
        const r = await api.post('/flow-triggers', form);
        setTriggers(ts => [r.data, ...ts]);
        toast.success('Trigger created');
      }
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  }

  async function toggleActive(t) {
    try {
      const r = await api.put(`/flow-triggers/${t._id}`, { active: !t.active });
      setTriggers(ts => ts.map(x => x._id === t._id ? r.data : x));
    } catch { toast.error('Failed to update'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this trigger?')) return;
    try {
      await api.delete(`/flow-triggers/${id}`);
      setTriggers(ts => ts.filter(t => t._id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const filteredFlows = metaFlows.filter(f =>
    !flowSearch || f.name.toLowerCase().includes(flowSearch.toLowerCase()) || f.id.includes(flowSearch)
  );

  const selectedFlow = metaFlows.find(f => f.id === form.flow_id);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Flow Triggers</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
            When a customer sends a keyword, automatically reply with a WhatsApp Flow form.
            Completed forms are saved to the contact's profile.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ New Trigger</button>
      </div>

      {/* How it works */}
      <div className="card" style={{ marginBottom: 20, background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)' }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {[
            { icon: '📩', title: 'Customer sends keyword', desc: 'e.g. "REGISTER" or "INFO" via WhatsApp' },
            { icon: '⚡', title: 'Bot sends Flow message', desc: 'An interactive form opens inside WhatsApp' },
            { icon: '✅', title: 'Customer fills the form', desc: 'Taps Submit inside the Flow' },
            { icon: '📋', title: 'Data saved to Contacts', desc: 'Visible in the Contacts page with a "Flow Data" badge' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: '1 1 160px' }}>
              <div style={{ fontSize: 22 }}>{s.icon}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trigger list */}
      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>
        ) : triggers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">⚡</div>
            <div className="empty-state-text">No triggers yet — create one to get started</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Keyword</th>
                  <th>Flow Name</th>
                  <th>Flow ID</th>
                  <th>CTA Button</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {triggers.map(t => (
                  <tr key={t._id}>
                    <td>
                      <code style={{ background: 'var(--surface2)', padding: '2px 8px', borderRadius: 4, fontSize: 13, fontWeight: 600, color: 'var(--primary-light)' }}>
                        {t.keyword}
                      </code>
                    </td>
                    <td style={{ fontWeight: 500 }}>{t.flow_name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{t.flow_id}</td>
                    <td style={{ fontSize: 13 }}>{t.flow_cta}</td>
                    <td>
                      <button
                        onClick={() => toggleActive(t)}
                        style={{
                          background: t.active ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.12)',
                          color: t.active ? 'var(--success)' : 'var(--text-muted)',
                          border: `1px solid ${t.active ? 'var(--success)' : 'var(--border)'}`,
                          borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {t.active ? '● Active' : '○ Paused'}
                      </button>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)} style={{ marginRight: 6 }}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-title">{editing ? 'Edit Trigger' : 'New Flow Trigger'}</div>
            <form onSubmit={handleSave}>

              {/* ── Keyword ─────────────────────────────────────────── */}
              <div className="form-group">
                <label className="label">Keyword <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input className="input" required placeholder="e.g. REGISTER"
                  value={form.keyword} onChange={e => set('keyword', e.target.value.toUpperCase())}
                  style={{ fontWeight: 600, letterSpacing: '0.04em' }} />
                <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>
                  Case-insensitive. Triggered when a customer sends this exact word.
                </div>
              </div>

              {/* ── Flow picker ──────────────────────────────────────── */}
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label className="label" style={{ margin: 0 }}>WhatsApp Flow <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {!manualId && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={loadMetaFlows} disabled={flowsLoading}>
                        {flowsLoading ? '⏳ Loading…' : '🔄 Refresh'}
                      </button>
                    )}
                    <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => { setManualId(m => !m); if (manualId) loadMetaFlows(); }}>
                      {manualId ? '← Browse Flows' : '✏️ Enter ID manually'}
                    </button>
                  </div>
                </div>

                {/* Currently selected flow pill */}
                {form.flow_id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 18 }}>⚡</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{form.flow_name || 'Selected Flow'}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>{form.flow_id}</div>
                    </div>
                    {selectedFlow && (
                      <span style={{ ...(STATUS_COLORS[selectedFlow.status] || STATUS_COLORS.DRAFT), padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
                        {STATUS_COLORS[selectedFlow.status]?.label || selectedFlow.status}
                      </span>
                    )}
                    <button type="button" onClick={() => set('flow_id', '') || set('flow_name', '')}
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>
                      ✕
                    </button>
                  </div>
                )}

                {/* Manual ID input */}
                {manualId ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label className="label" style={{ fontSize: 11 }}>Flow ID</label>
                      <input className="input" required={!form.flow_id} placeholder="e.g. 1234567890"
                        value={form.flow_id} onChange={e => set('flow_id', e.target.value)}
                        style={{ fontFamily: 'monospace' }} />
                    </div>
                    <div>
                      <label className="label" style={{ fontSize: 11 }}>Display Name</label>
                      <input className="input" required placeholder="e.g. Registration Form"
                        value={form.flow_name} onChange={e => set('flow_name', e.target.value)} />
                    </div>
                  </div>
                ) : (
                  /* Flow list from Meta */
                  <div>
                    {flowsError && !flowsLoading && (
                      <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', borderRadius: 8, fontSize: 13, color: 'var(--danger)', marginBottom: 8 }}>
                        {flowsError}
                      </div>
                    )}

                    {flowsLoading && (
                      <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: 13 }}>
                        Loading your flows from Meta…
                      </div>
                    )}

                    {!flowsLoading && metaFlows.length > 0 && (
                      <>
                        {metaFlows.length > 5 && (
                          <input className="input" placeholder="Search flows by name or ID…"
                            value={flowSearch} onChange={e => setFlowSearch(e.target.value)}
                            style={{ marginBottom: 8 }} />
                        )}
                        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
                          {filteredFlows.length === 0 ? (
                            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No flows match your search</div>
                          ) : filteredFlows.map((flow, i) => {
                            const isSelected = form.flow_id === flow.id;
                            const sc = STATUS_COLORS[flow.status] || STATUS_COLORS.DRAFT;
                            return (
                              <div key={flow.id}
                                onClick={() => selectFlow(flow)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 12,
                                  padding: '11px 14px',
                                  borderBottom: i < filteredFlows.length - 1 ? '1px solid var(--border)' : 'none',
                                  background: isSelected ? 'rgba(99,102,241,0.08)' : 'transparent',
                                  cursor: 'pointer', transition: 'background 0.1s',
                                }}
                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--surface2)'; }}
                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                              >
                                <div style={{ fontSize: 20, flexShrink: 0 }}>
                                  {isSelected ? '✅' : '📋'}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, fontSize: 14, color: isSelected ? 'var(--primary-light)' : 'var(--text)' }}>
                                    {flow.name}
                                  </div>
                                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                                    ID: {flow.id}
                                    {flow.categories?.length > 0 && (
                                      <span style={{ marginLeft: 8, fontFamily: 'inherit' }}>· {flow.categories.join(', ')}</span>
                                    )}
                                  </div>
                                </div>
                                <span style={{ background: sc.bg, color: sc.color, padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                                  {sc.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                          {metaFlows.length} flow{metaFlows.length !== 1 ? 's' : ''} on your account · Can't find yours?{' '}
                          <button type="button" onClick={() => setManualId(true)}
                            style={{ background: 'none', border: 'none', color: 'var(--primary-light)', cursor: 'pointer', fontSize: 11, padding: 0, textDecoration: 'underline' }}>
                            Enter the ID manually
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* ── Message fields (only shown once a flow is chosen) ── */}
              {form.flow_id && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="form-group">
                    <label className="label">Button Text (CTA)</label>
                    <input className="input" placeholder="Open Form"
                      value={form.flow_cta} onChange={e => set('flow_cta', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="label">Message Header <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                    <input className="input" placeholder="e.g. Welcome!"
                      value={form.flow_header} onChange={e => set('flow_header', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="label">Message Body</label>
                    <textarea className="textarea" rows={2}
                      value={form.flow_body} onChange={e => set('flow_body', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
                      <span className="label" style={{ margin: 0 }}>Active (trigger is live)</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving || !form.flow_id || !form.keyword}>
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Trigger'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
